import { ListComponentBaseProps, toasterService, useInterval } from '@hevoio/hevo-ui-toolkit';
import { useEffect, useRef, useState } from 'react';
import styles from './ConnectivityCards.module.scss';
import { PeeringCard } from './PeeringCard';
import { AcceptConnectionDrawer } from './AcceptConnectionDrawer/AcceptConnectionDrawer';
import { VPCPeeringModal } from './CreateVPCModal/VPCPeeringModal';
import { PEERING_POLL_INTERVAL } from '../constants/poll';
import { CONNECTIVITY_CARDS_DATA_ID } from '../dataIds';
import { VPCPeeringConnection, VPCPeeringFormMode } from '../interface';
import { useConfirm } from '../../../../modal/components/confirm-dialog-v1/ConfirmDialogV1Provider';
import TeamAPI from '../../api/TeamAPI';
import { getErrorMessageFromObj } from '../../../../core/utils/request';
import useUserService from '../../../../auth/hooks/useUserService';

export interface ConnectivityCardsProps {
  onActionComplete?: () => void;
  updateMemoryData?: (data: VPCPeeringConnection[] | undefined) => void;
}

interface PeeringFormState {
  mode: VPCPeeringFormMode;
  connection: VPCPeeringConnection;
}

export function ConnectivityCards({
  data,
  refresh,
  writeData,
  onActionComplete,
  updateMemoryData
}: ConnectivityCardsProps & ListComponentBaseProps<VPCPeeringConnection[]>) {
  const [acceptConnection, setAcceptConnection] = useState<VPCPeeringConnection | null>(null);
  const [formState, setFormState] = useState<PeeringFormState | null>(null);
  const { confirm } = useConfirm();
  const { user } = useUserService();

  const connections = data ?? [];

  // A tick outlives whatever started it: `useInterval` does not await the
  // previous promise and the request carries no abort signal. Both write
  // targets live in the parent, which never unmounts, so a response landing
  // after a delete would resurrect the deleted row — and the in-memory cache
  // is read-first, so it would keep serving that stale list without a refetch.
  const pollGeneration = useRef(0);
  const isPolling = useRef(false);

  // A form open over the list holds a click-time snapshot of its connection, so
  // a tick cannot disturb it — but a full reset unmounts this component and
  // would close the form with no save and no warning.
  const isFormOpen = !!formState || !!acceptConnection;
  const isFormOpenRef = useRef(isFormOpen);
  isFormOpenRef.current = isFormOpen;

  useEffect(
    () => () => {
      // Invalidate anything still in flight at unmount.
      pollGeneration.current += 1;
    },
    []
  );

  useInterval(() => {
    if (isPolling.current) {
      // Skip rather than queue: overlapping ticks resolve last-wins, which can
      // apply an older list over a newer one.
      return;
    }

    const generation = pollGeneration.current;
    isPolling.current = true;

    TeamAPI.getConnectivityPeeringList(user.teamId)
      .then((res: { data: VPCPeeringConnection[] }) => {
        if (generation !== pollGeneration.current) {
          return;
        }

        const fresh = res?.data ?? [];

        if (!fresh.length) {
          // Only a full reset moves the list into its empty state, and that
          // unmounts this component — so never do it under an open form.
          if (!isFormOpenRef.current) {
            (onActionComplete ?? refresh)?.();
          }
          return;
        }

        // writeData swaps the rows in place. A reset would push the list into
        // its LOADING state, which unmounts this component and takes the edit
        // modal and the accept-connection drawer down with it.
        updateMemoryData?.(fresh);
        writeData?.(() => fresh);
      })
      .catch(() => {
        // A background poll failing is not worth interrupting the user for —
        // the next tick retries.
      })
      .finally(() => {
        isPolling.current = false;
      });
  }, PEERING_POLL_INTERVAL);

  // The card's onDelete returns nothing, so the confirm chain is deliberately
  // not returned to the caller.
  const onDelete = (connection: VPCPeeringConnection) => {
    confirm({
      title: 'Delete VPC Peering Connection',
      body: connection.peering_id
        ? `This tears down the peering connection ${connection.peering_id} in AWS. The request cannot be restored — a new one has to be created.`
        : 'This cancels the request. It cannot be restored — a new one has to be created.',
      positiveButtonText: 'Delete',
      negativeButtonText: 'Cancel'
    }).then(confirmed => {
      if (!confirmed) {
        return Promise.resolve();
      }

      return TeamAPI.deletePeeringRequest(connection.id)
        .then((res: { message?: string }) => {
          pollGeneration.current += 1;
          toasterService.pop('success', res?.message ?? 'Delete accepted');
          (onActionComplete ?? refresh)?.();
        })
        .catch(error => {
          toasterService.pop('error', getErrorMessageFromObj(error));
        });
    });
  };

  // The mode comes from the card, which derives it from the granted action.
  // Recomputing it from status here could open the form in a mode the server
  // never granted — submitting a PATCH where only resubmit is allowed.
  //
  // The captured `connection` is what makes an open form immune to poll ticks:
  // the modal reads from this snapshot, not from `data`. VPCPeeringModal sets
  // `enableReinitialize`, so passing a live row here would wipe typed input on
  // every tick.
  const onEdit = (connection: VPCPeeringConnection, mode: VPCPeeringFormMode) =>
    setFormState({ mode, connection });

  return (
    <>
      <div className={styles.cardList} data-id={CONNECTIVITY_CARDS_DATA_ID}>
        {connections.map(connection => (
          <PeeringCard
            key={connection.id}
            connection={connection}
            onEdit={onEdit}
            onDelete={onDelete}
            onHowToAccept={setAcceptConnection}
          />
        ))}
      </div>

      <AcceptConnectionDrawer
        open={!!acceptConnection}
        connection={acceptConnection}
        onClose={() => setAcceptConnection(null)}
      />

      {formState && (
        <VPCPeeringModal
          key={`${formState.mode}-${formState.connection.id}`}
          open
          mode={formState.mode}
          connection={formState.connection}
          onClose={() => setFormState(null)}
          onSuccess={() => {
            pollGeneration.current += 1;
            (onActionComplete ?? refresh)?.();
          }}
        />
      )}
    </>
  );
}
